import React, { useState, useEffect, useCallback } from "react";
import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { SearchHistoryGrid } from "./SearchHistoryGrid";
import { useReceptionBookSearchHistory } from "../hooks/useReceptionBookSearchHistory";
import { type UserSearchHistoryReceptionBookResponseType } from "../types";

interface SearchHistoryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (data: UserSearchHistoryReceptionBookResponseType) => void;
}

export const SearchHistoryModal: React.FC<SearchHistoryModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // モーダル内で検索履歴データを管理するための状態を追加
  const [modalHistoryData, setModalHistoryData] = useState<
    UserSearchHistoryReceptionBookResponseType[]
  >([]);
  const [modalTotalCount, setModalTotalCount] = useState(0);

  // SWRによる自動フェッチを防止するためfalseを渡す
  const { isError, refetch } = useReceptionBookSearchHistory(false);

  // モーダルが開かれるたびにデータを取得
  useEffect(() => {
    if (open) {
      void fetchData();
    }
  }, [open]);

  const fetchData = useCallback(async () => {
    if (open) {
      setIsModalLoading(true);
      try {
        const result = await refetch();
        // 取得データをモーダル内の状態に保存
        setModalHistoryData(result);
        setModalTotalCount(result.length);
      } catch (error) {
        console.error("データ取得エラー:", error);
      } finally {
        setIsModalLoading(false);
      }
    }
  }, [open, refetch]);

  // ページサイズの変更ハンドラ
  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(0);
  };

  // ページ変更ハンドラ
  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={(
        event: Record<string, unknown>,
        reason: "backdropClick" | "escapeKeyDown"
      ) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      ariaLabelledby="search-history-modal"
      ariaDescribedby="search-history-modal-description"
      width={"80%"}
      height={"80%"}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <PageTitle>検索履歴</PageTitle>
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "calc(100% - 100px)", // ヘッダーとフッターの高さを考慮
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", width: "100%" }}>
          {isModalLoading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ my: "20px" }} />
            </Stack>
          ) : isError ? (
            <Stack alignItems="center">
              <Typography color="error" sx={{ my: "20px" }}>
                データの取得に失敗しました。
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  void fetchData();
                }}
              >
                再試行
              </Button>
            </Stack>
          ) : (
            <SearchHistoryGrid
              loading={false}
              rows={modalHistoryData}
              onRowClick={(data) => {
                onSelect(data);
                onClose();
              }}
              page={page}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 25, 50]}
              rowCount={modalTotalCount}
            />
          )}
        </Box>

        <Box
          sx={{
            mt: 2,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ※ 行を選択すると検索フォームに反映されます
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            ※ ...で省略されている箇所はホバーすると表示されます
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            ※ 契約していないプランの検索条件はフォームに反映されません
          </Typography>
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Button variant="outlined" onClick={onClose}>
            閉じる
          </Button>
        </Stack>
      </Box>
    </CustomModal>
  );
};
